import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import theme from "../../../../../Layouts/theme";
import sh from "bundles/common/utils/sh";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import CandidateDialog from "bundles/common/components/CandidateDialog";
import HiredPositionSelectionModal from "bundles/common/components/HiredPositionSelectionModal";
import { useSnackbar } from "notistack";

import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { reorder } from "@atlaskit/pragmatic-drag-and-drop/reorder";
import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { getReorderDestinationIndex } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index";

import KanbanCard from "./KanbanCard";
import KanbanColumn from "./KanbanColumn";
import ColumnDropIndicator from "./ColumnDropIndicator";

const PROTECTED_COLUMN_KEYS = ["pending", "hired"];

const toKey = (id) => String(id);

const getActiveApplications = (jobApplications = []) => {
  const apps = Array.isArray(jobApplications) ? jobApplications : [];
  return apps.filter((app) => app?.status !== "closed");
};

const isSameCardDropTarget = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.type === b.type &&
    String(a.columnId) === String(b.columnId) &&
    a.index === b.index &&
    a.edge === b.edge
  );
};

const KanbanBoard = ({
  columns = [],
  setColumns,
  columnStatsById = {},
  columnCountsById = {},
  cardsByColumnId = {},
  setCardsByColumnId,
  dragDisabled = false,
  onKanbanMetaUpdate,
  onApplicationClosed,
  onBoardMutated,
}) => {
  const intl = useIntl();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [advancingCardId, setAdvancingCardId] = useState(null);
  const [draggingCardId, setDraggingCardId] = useState(null);
  const [cardDropTarget, setCardDropTarget] = useState(null); // { type: 'card'|'column', columnId, index?, edge? }
  const [draggingColumnId, setDraggingColumnId] = useState(null);
  const [columnDropTarget, setColumnDropTarget] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogJobApplication, setDialogJobApplication] = useState(null);
  const [dialogCandidate, setDialogCandidate] = useState(null);
  const boardScrollRef = useRef(null);
  const [editingColumnId, setEditingColumnId] = useState(null);
  
  // Hired selection modal state
  const [hiredModalOpen, setHiredModalOpen] = useState(false);
  const [hiredModalData, setHiredModalData] = useState(null); // { card, fromColumnId, toColumnId, toIndex, prevSnapshot, isColumnChange }
  const [selectedHiredAppId, setSelectedHiredAppId] = useState(null);
  const [hiredModalLoading, setHiredModalLoading] = useState(false);

  const cardsByColumnIdRef = useRef(cardsByColumnId);
  const columnsRef = useRef(columns);
  const cardDropTargetRef = useRef(null);

  const dragTargetRafRef = useRef(null);
  const pendingDragTargetRef = useRef(null);

  const normalized = useMemo(() => {
    const next = { ...(cardsByColumnId || {}) };
    (columns || []).forEach((c) => {
      const k = toKey(c.id);
      if (!Array.isArray(next[k])) next[k] = [];
    });
    return next;
  }, [cardsByColumnId, columns]);

  const closeHiredModal = useCallback(() => {
    setHiredModalOpen(false);
    setHiredModalData(null);
    setSelectedHiredAppId(null);
    setHiredModalLoading(false);
  }, []);

  const openHiredModal = useCallback((data, defaultSelectedId = null) => {
    setHiredModalData(data);
    setSelectedHiredAppId(defaultSelectedId);
    setHiredModalOpen(true);
  }, []);

  const optimisticallyMoveCard = useCallback(
    ({ cardId, fromColumnId, toColumnId, toIndex, isColumnChange, patchMovedCard }) => {
      setCardsByColumnId((prev) => {
        const next = { ...(prev || {}) };
        const fromArr = Array.isArray(next[fromColumnId]) ? [...next[fromColumnId]] : [];
        const toArr = fromColumnId === toColumnId ? fromArr : Array.isArray(next[toColumnId]) ? [...next[toColumnId]] : [];

        const idx = fromArr.findIndex((c) => c?.id === cardId);
        if (idx === -1) return prev;

        if (fromColumnId === toColumnId) {
          next[fromColumnId] = reorder({ list: fromArr, startIndex: idx, finishIndex: toIndex });
          return next;
        }

        const [movedCard] = fromArr.splice(idx, 1);
        const safeIndex = Math.min(Math.max(toIndex, 0), toArr.length);

        const patched = typeof patchMovedCard === "function" ? patchMovedCard(movedCard) : movedCard;
        toArr.splice(safeIndex, 0, {
          ...(patched || movedCard),
          kanban_column_id: Number(toColumnId),
          ...(isColumnChange ? { last_moved_at: new Date().toISOString() } : {}),
        });

        next[fromColumnId] = fromArr;
        next[toColumnId] = toArr;
        return next;
      });
    },
    [setCardsByColumnId]
  );

  // Execute the actual card move API call
  const executeCardMove = useCallback(
    async (cardId, toColumnId, toIndex, prevSnapshot, hiredJobApplicationId = null) => {
      try {
        const payload = {
          kanban_column_id: Number(toColumnId),
          position: toIndex,
          authenticity_token: authenticityToken,
        };

        if (hiredJobApplicationId) {
          payload.hired_job_application_id = hiredJobApplicationId;
        }

        const res = await sh.put(`/move_kanban_card/${cardId}`, payload);

        const meta = res?.data?.kanban;
        if (meta && typeof onKanbanMetaUpdate === "function") onKanbanMetaUpdate(meta);
        if (typeof onBoardMutated === "function") onBoardMutated();

        return true;
      } catch (e) {
        console.error(e);
        setCardsByColumnId(prevSnapshot || {});
        enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.move_failed" }), { variant: "error" });
        return false;
      }
    },
    [authenticityToken, onKanbanMetaUpdate, onBoardMutated, setCardsByColumnId, enqueueSnackbar, intl]
  );

  const handleDrop = useCallback(
    async ({ source, location }, lastDropTarget = null) => {
      const sourceData = source?.data || {};
      if (sourceData.type !== "kanban-card") return;

      const cardId = sourceData.cardId;
      const fromColumnId = toKey(sourceData.fromColumnId);

      const dropTargets = location?.current?.dropTargets || [];
      let cardTarget = dropTargets.find((t) => t?.data?.type === "kanban-card-target");
      const columnTarget = dropTargets.find((t) => t?.data?.type === "kanban-column-target");

      const fallbackTarget = lastDropTarget?.type === "card" ? lastDropTarget : null;

      const toColumnId = toKey(
        cardTarget?.data?.columnId ?? fallbackTarget?.columnId ?? columnTarget?.data?.columnId
      );

      const current = cardsByColumnIdRef.current || {};
      const fromList = Array.isArray(current[fromColumnId]) ? current[fromColumnId] : [];
      const destList = Array.isArray(current[toColumnId]) ? current[toColumnId] : [];

      const isCrossColumnMove = fromColumnId !== toColumnId;

      if (!cardTarget && !fallbackTarget) {
        if (!isCrossColumnMove || !columnTarget) return;
      }

      const actualFromIndex = fromList.findIndex((c) => c?.id === cardId);
      if (actualFromIndex === -1) return;

      let toIndex = destList.length;

      if (cardTarget || fallbackTarget) {
        const targetIndex = cardTarget?.data?.index ?? fallbackTarget?.index;
        const edge = cardTarget ? extractClosestEdge(cardTarget.data) : fallbackTarget?.edge;
        const after = edge === "bottom";

        if (fromColumnId === toColumnId) {
          toIndex = getReorderDestinationIndex({
            startIndex: actualFromIndex,
            indexOfTarget: targetIndex,
            closestEdgeOfTarget: edge,
            axis: "vertical",
          });
        } else {
          toIndex = targetIndex + (after ? 1 : 0);
        }
      }

      if (fromColumnId === toColumnId && toIndex === actualFromIndex) return;
      const isColumnChange = fromColumnId !== toColumnId;

      const prevSnapshot = cardsByColumnIdRef.current;
      const card = fromList[actualFromIndex];

      // Check if moving to "hired" column and card has multiple job applications
      const currentColumns = columnsRef.current || [];
      const targetColumn = currentColumns.find((c) => toKey(c.id) === toColumnId);
      const isMovingToHired = targetColumn?.key === "hired";
      const jobApplications = card?.job_applications || [];
      const activeApplications = getActiveApplications(jobApplications);
      const hiredStatus = card?.is_external ? "hired" : "accepted";

      // Only ask which application was accepted when there is genuinely a choice; a single
      // active one (spontaneous or not) is hired as it stands.
      if (isMovingToHired && activeApplications.length > 1) {
        openHiredModal(
          {
            card,
            fromColumnId,
            toColumnId,
            toIndex,
            prevSnapshot,
            isColumnChange,
          },
          activeApplications[0]?.id || null
        );
        return;
      }

      let updatedJobApplications = jobApplications;
      if (isMovingToHired && activeApplications.length === 1) {
        updatedJobApplications = jobApplications
          .map((app) => ({
            ...app,
            status: app.id === activeApplications[0].id ? hiredStatus : "closed",
          }))
          .filter((app) => app.status === hiredStatus);
      }

      setCardsByColumnId((prev) => {
        const next = { ...(prev || {}) };
        const fromArr = Array.isArray(next[fromColumnId]) ? [...next[fromColumnId]] : [];
        const toArr =
          fromColumnId === toColumnId
            ? fromArr
            : Array.isArray(next[toColumnId])
              ? [...next[toColumnId]]
              : [];

        const idx = fromArr.findIndex((c) => c?.id === cardId);
        if (idx === -1) return prev;

        if (fromColumnId === toColumnId) {
          next[fromColumnId] = reorder({ list: fromArr, startIndex: idx, finishIndex: toIndex });
          return next;
        }

        const [movedCard] = fromArr.splice(idx, 1);
        const safeIndex = Math.min(Math.max(toIndex, 0), toArr.length);
        toArr.splice(safeIndex, 0, {
          ...movedCard,
          kanban_column_id: Number(toColumnId),
          ...(isMovingToHired ? { job_applications: updatedJobApplications } : {}),
          ...(isColumnChange ? { last_moved_at: new Date().toISOString() } : {}),
        });
        next[fromColumnId] = fromArr;
        next[toColumnId] = toArr;
        return next;
      });

      // Execute move directly (single or no applications)
      await executeCardMove(cardId, toColumnId, toIndex, prevSnapshot);
    },
    [setCardsByColumnId, executeCardMove, openHiredModal]
  );
  
  // Handle hired modal confirmation
  const handleHiredConfirm = useCallback(async () => {
    if (!hiredModalData || !selectedHiredAppId) return;

    setHiredModalLoading(true);

    const { card, fromColumnId, toColumnId, toIndex, prevSnapshot, isColumnChange } = hiredModalData;

    try {
      const updatedJobApplications = (card.job_applications || [])
        .map((app) => ({
          ...app,
          status: app.id === selectedHiredAppId ? (card?.is_external ? "hired" : "accepted") : "closed",
        }))
        .filter((app) => app.id === selectedHiredAppId);

      optimisticallyMoveCard({
        cardId: card.id,
        fromColumnId,
        toColumnId,
        toIndex,
        isColumnChange,
        patchMovedCard: (movedCard) => ({
          ...movedCard,
          job_applications: updatedJobApplications,
        }),
      });

      const success = await executeCardMove(card.id, toColumnId, toIndex, prevSnapshot, selectedHiredAppId);
      if (success) enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.marked_hired" }), { variant: "success" });
      closeHiredModal();
    } catch (e) {
      console.error(e);
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.cv_theque.error_adding_to_kanban" }), { variant: "error" });
      setCardsByColumnId(hiredModalData?.prevSnapshot || {});
      setHiredModalLoading(false);
    }
  }, [
    closeHiredModal,
    enqueueSnackbar,
    executeCardMove,
    hiredModalData,
    intl,
    optimisticallyMoveCard,
    selectedHiredAppId,
    setCardsByColumnId,
  ]);

  // Handle hired modal cancel - just close the modal, card hasn't moved yet
  const handleHiredCancel = useCallback(() => {
    closeHiredModal();
  }, [closeHiredModal]);
  
  const handleToggleFavorite = useCallback(
    (candidateId) => {
      const prev = currentRecruiter?.favorite_candidate_ids || [];
      const isAlreadySaved = prev.some((id) => String(id) === String(candidateId));
      const candidateIdNum = Number(candidateId);
      const next = isAlreadySaved
        ? prev.filter((id) => String(id) !== String(candidateId))
        : [...prev, Number.isFinite(candidateIdNum) ? candidateIdNum : candidateId];

      dispatch({ type: "UPDATE_CURRENT_RECRUITER", payload: { ...(currentRecruiter || {}), favorite_candidate_ids: next } });

      sh.put("/update_favorite_candidates", {
        authenticity_token: authenticityToken,
        favorite_candidates: next.map((id) => String(id)),
      })
        .then((res) => {
          if (res?.data) {
            dispatch({ type: "UPDATE_CURRENT_RECRUITER", payload: res.data });
          }
        })
        .catch((e) => {
          console.error(e);
          dispatch({ type: "UPDATE_CURRENT_RECRUITER", payload: { ...(currentRecruiter || {}), favorite_candidate_ids: prev } });
        });
    },
    [authenticityToken, currentRecruiter, dispatch]
  );

  const openDialogForCard = useCallback((jobApplication, candidate) => {
    setDialogJobApplication(jobApplication);
    setDialogCandidate(candidate);
    setIsDialogOpen(true);
  }, []);

  const pendingNextColumnName = useMemo(() => {
    const cols = Array.isArray(columns) ? columns : [];
    const pendingIdx = cols.findIndex((c) => c?.key === "pending");
    if (pendingIdx === -1) return null;
    const next = cols[pendingIdx + 1];
    return next?.name || null;
  }, [columns]);

  const advanceJobApplication = useCallback(
    async (jobApp) => {
      if (!jobApp?.id) return jobApp;
      if (!authenticityToken) return jobApp;
      if (advancingCardId) return jobApp;

      const fromColumnId = toKey(jobApp?.kanban_column_id);
      setAdvancingCardId(jobApp.id);

      try {
        const res = await sh.put(`/advance_kanban_card/${jobApp.id}`, {
          authenticity_token: authenticityToken,
        });
        const payload = res?.data || {};
        const meta = payload?.kanban;
        const updated = payload?.data || payload;
        const advanced = Boolean(payload?.advanced);
        const toColumnId = toKey(payload?.to_kanban_column_id || updated?.kanban_column_id);

        if (!advanced) {
          enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.already_last_step" }), { variant: "info" });
          return { ...(jobApp || {}), ...(updated || {}) };
        }

        setCardsByColumnId((prev) => {
          const next = { ...(prev || {}) };
          const fromArr = Array.isArray(next[fromColumnId]) ? [...next[fromColumnId]] : [];
          const toArr = Array.isArray(next[toColumnId]) ? [...next[toColumnId]] : [];
          const idx = fromArr.findIndex((c) => c?.id === jobApp.id);
          if (idx === -1) return prev;
          const [moved] = fromArr.splice(idx, 1);
          toArr.unshift({
            ...(moved || jobApp),
            ...(updated || {}),
            kanban_column_id: Number(toColumnId),
            last_moved_at: new Date().toISOString(),
          });
          next[fromColumnId] = fromArr;
          next[toColumnId] = toArr;
          return next;
        });

        if (meta && typeof onKanbanMetaUpdate === "function") onKanbanMetaUpdate(meta);
        if (typeof onBoardMutated === "function") onBoardMutated();

        return {
          ...(jobApp || {}),
          ...(updated || {}),
          kanban_column_id: Number(toColumnId),
          last_moved_at: new Date().toISOString(),
        };
      } catch (err) {
        console.error(err);
        enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.advance_failed" }), { variant: "error" });
        return jobApp;
      } finally {
        setAdvancingCardId(null);
      }
    },
    [advancingCardId, authenticityToken, enqueueSnackbar, intl, onKanbanMetaUpdate, onBoardMutated, setCardsByColumnId]
  );

  // Auto-scroll the horizontal kanban container while dragging near the left/right edges.
  // This fixes the UX where dragging to off-screen columns is difficult once overflowX kicks in.
  // Also auto-scrolls the page vertically when dragging near the top/bottom of the viewport.
  useEffect(() => {
    if (!draggingCardId) return undefined;

    let rafId = null;
    let lastEvent = null;

    const tick = () => {
      rafId = null;
      if (!lastEvent) return;

      const x = lastEvent.clientX;
      const y = lastEvent.clientY;

      // --- Horizontal scroll (kanban container) ---
      const container = boardScrollRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();

        const hThreshold = 90;
        const hMaxSpeed = 26;

        let dx = 0;
        const rightStart = rect.right - hThreshold;
        const leftEnd = rect.left + hThreshold;

        if (x >= rightStart) {
          const t = Math.min(1, Math.max(0, (x - rightStart) / hThreshold));
          dx = Math.ceil(t * hMaxSpeed);
        } else if (x <= leftEnd) {
          const t = Math.min(1, Math.max(0, (leftEnd - x) / hThreshold));
          dx = -Math.ceil(t * hMaxSpeed);
        }

        if (dx !== 0) container.scrollLeft += dx;
      }

      // --- Vertical scroll (page) ---
      const vThreshold = 150;
      const vMaxSpeed = 20;

      let dy = 0;
      if (y <= vThreshold) {
        const t = Math.min(1, Math.max(0, (vThreshold - y) / vThreshold));
        dy = -Math.ceil(t * vMaxSpeed);
      } else if (y >= window.innerHeight - vThreshold) {
        const t = Math.min(1, Math.max(0, (y - (window.innerHeight - vThreshold)) / vThreshold));
        dy = Math.ceil(t * vMaxSpeed);
      }

      if (dy !== 0) window.scrollBy(0, dy);
    };

    const onDragOver = (e) => {
      lastEvent = e;
      if (rafId == null) rafId = window.requestAnimationFrame(tick);
    };
    
    window.addEventListener("dragover", onDragOver);

    return () => {
      window.removeEventListener("dragover", onDragOver);
      if (rafId != null) window.cancelAnimationFrame(rafId);
    };
  }, [draggingCardId]);
  
  useEffect(() => {
    if (dragDisabled) return undefined;

    return monitorForElements({
      canMonitor: ({ source }) => source?.data?.type === "kanban-card",
      onDragStart: ({ source }) => setDraggingCardId(source?.data?.cardId || null),
      onDrag: ({ location }) => {
        const dropTargets = location?.current?.dropTargets || [];
        const cardTarget = dropTargets.find((t) => t?.data?.type === "kanban-card-target");
        const columnTarget = dropTargets.find((t) => t?.data?.type === "kanban-column-target");

        let nextTarget = null;

        if (cardTarget) {
          const edge = extractClosestEdge(cardTarget.data);
          nextTarget = {
            type: "card",
            columnId: toKey(cardTarget.data.columnId),
            index: cardTarget.data.index,
            edge,
          };
        } else if (columnTarget) {
          nextTarget = {
            type: "column",
            columnId: toKey(columnTarget.data.columnId),
          };
        }

        if (nextTarget) cardDropTargetRef.current = nextTarget;

        pendingDragTargetRef.current = nextTarget;
        if (dragTargetRafRef.current != null) return;

        dragTargetRafRef.current = window.requestAnimationFrame(() => {
          dragTargetRafRef.current = null;
          const t = pendingDragTargetRef.current;
          setCardDropTarget((prev) => (isSameCardDropTarget(prev, t) ? prev : t));
        });
      },
      onDrop: (args) => {
        const lastDropTarget = cardDropTargetRef.current;
        setDraggingCardId(null);
        setCardDropTarget(null);
        cardDropTargetRef.current = null;
        if (dragTargetRafRef.current != null) {
          window.cancelAnimationFrame(dragTargetRafRef.current);
          dragTargetRafRef.current = null;
        }
        pendingDragTargetRef.current = null;
        handleDrop(args, lastDropTarget);
      },
    });
  }, [dragDisabled, handleDrop]);
  
  const handleColumnDrop = useCallback(
    async ({ source, location }) => {
      const sourceData = source?.data || {};
      if (sourceData.type !== "kanban-column") return;

      const draggedColumnId = sourceData.columnId;
      const currentColumns = columnsRef.current || [];
      const draggedColumn = currentColumns.find((c) => c.id === draggedColumnId);
      if (!draggedColumn) return;

      if (PROTECTED_COLUMN_KEYS.includes(draggedColumn.key)) return;

      const dropTargets = location?.current?.dropTargets || [];
      const columnTarget = dropTargets.find((t) => t?.data?.type === "kanban-column-drop-target");
      if (!columnTarget) return;

      const targetColumnId = columnTarget.data.columnId;
      const edge = extractClosestEdge(columnTarget.data);
      const targetColumn = currentColumns.find((c) => c.id === targetColumnId);
      if (!targetColumn) return;

      const fromIndex = currentColumns.findIndex((c) => c.id === draggedColumnId);
      const targetIndex = currentColumns.findIndex((c) => c.id === targetColumnId);
      if (fromIndex === -1 || targetIndex === -1) return;

      let toIndex = getReorderDestinationIndex({
        startIndex: fromIndex,
        indexOfTarget: targetIndex,
        closestEdgeOfTarget: edge,
        axis: "horizontal",
      });

      const pendingIndex = currentColumns.findIndex((c) => c.key === "pending");
      const hiredIndex = currentColumns.findIndex((c) => c.key === "hired");
      if (pendingIndex !== -1 && toIndex <= pendingIndex) toIndex = pendingIndex + 1;
      if (hiredIndex !== -1 && toIndex >= hiredIndex) toIndex = hiredIndex - 1;

      if (toIndex === fromIndex) return;

      const prevColumns = [...currentColumns];

      setColumns((prev) => {
        const next = reorder({ list: [...(prev || [])], startIndex: fromIndex, finishIndex: toIndex });
        return next.map((col, idx) => ({ ...col, position: idx + 1 }));
      });

      try {
        await sh.put(`/kanban_column/${draggedColumnId}`, {
          position: toIndex + 1,
          authenticity_token: authenticityToken,
        });
      } catch (e) {
        console.error(e);
        setColumns(prevColumns);
        enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.column_move_error" }), { variant: "error" });
      }
    },
    [authenticityToken, setColumns, enqueueSnackbar, intl]
  );

  const handleCardApplicationReopened = useCallback(
    (reopenedApp, sourceCard = null) => {
      const reopenedAppId = reopenedApp?.id;
      if (!reopenedAppId) return;

      const kanbanCardId = sourceCard?.id || dialogJobApplication?.id;
      if (!kanbanCardId) return;

      const newStatus = reopenedApp?.is_external ? "open" : "pending";

      setCardsByColumnId((prev) => {
        const next = { ...(prev || {}) };
        Object.keys(next).forEach((colId) => {
          const arr = Array.isArray(next[colId]) ? next[colId] : [];
          next[colId] = arr.map((card) => {
            if (card?.id !== kanbanCardId) return card;

            const updatedApps = (card.job_applications || []).map((app) =>
              app.id === reopenedAppId ? { ...app, status: newStatus } : app
            );

            return { ...card, job_applications: updatedApps };
          });
        });
        return next;
      });
    },
    [dialogJobApplication, setCardsByColumnId]
  );

  const handleCardApplicationClosed = useCallback(
    (closed, sourceCard = null, sourceCandidate = null) => {
      const closedPayload = typeof closed === "object" ? closed : {};
      const closedAppId = closedPayload?.id;
      const allClosed = Boolean(closedPayload?.all_closed);
      if (!closedAppId) return;

      const kanbanCardId = sourceCard?.id || dialogJobApplication?.id;
      if (!kanbanCardId) return;

      if (allClosed && typeof onApplicationClosed === "function") {
        const baseCard = sourceCard || dialogJobApplication || {};
        const baseApps = Array.isArray(closedPayload?.updated_job_applications)
          ? closedPayload.updated_job_applications
          : Array.isArray(baseCard?.job_applications)
            ? baseCard.job_applications.map((app) => ({ ...app, status: "closed" }))
            : [];

        onApplicationClosed({
          ...baseCard,
          status: "closed",
          candidate: sourceCandidate || dialogCandidate || baseCard?.candidate,
          job_applications: baseApps,
        });
      }

      setCardsByColumnId((prev) => {
        const next = { ...(prev || {}) };
        Object.keys(next).forEach((colId) => {
          const arr = Array.isArray(next[colId]) ? next[colId] : [];
          next[colId] = arr
            .map((card) => {
              if (card?.id !== kanbanCardId) return card;

              const updatedApps = (card.job_applications || []).map((app) =>
                app.id === closedAppId ? { ...app, status: "closed" } : app
              );

              if (allClosed) return null;
              return { ...card, job_applications: updatedApps };
            })
            .filter(Boolean);
        });
        return next;
      });
    },
    [dialogCandidate, dialogJobApplication, onApplicationClosed, setCardsByColumnId]
  );

  // Column drags
  useEffect(() => {
    if (dragDisabled) return undefined;

    return monitorForElements({
      canMonitor: ({ source }) => source?.data?.type === "kanban-column",
      onDragStart: ({ source }) => setDraggingColumnId(source?.data?.columnId || null),
      onDrag: ({ location }) => {
        const dropTargets = location?.current?.dropTargets || [];
        const columnTarget = dropTargets.find((t) => t?.data?.type === "kanban-column-drop-target");
        if (columnTarget) {
          const edge = extractClosestEdge(columnTarget.data);
          setColumnDropTarget({ columnId: columnTarget.data.columnId, edge });
        } else {
          setColumnDropTarget(null);
        }
      },
      onDrop: (args) => {
        setDraggingColumnId(null);
        setColumnDropTarget(null);
        handleColumnDrop(args);
      },
    });
  }, [dragDisabled, handleColumnDrop]);

  useEffect(() => {
    cardsByColumnIdRef.current = cardsByColumnId;
  }, [cardsByColumnId]);

  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  useEffect(() => {
    const pendingCol = (columns || []).find((c) => c?.key === "pending");
    if (!pendingCol) return;

    const pendingCount = (cardsByColumnId?.[toKey(pendingCol.id)] || []).length;
    const currentCount = currentRecruiter?.company?.kanban_pending_cards_count;

    if (!currentRecruiter?.company) return;
    if (Number(currentCount) === Number(pendingCount)) return;

    dispatch({
      type: "UPDATE_CURRENT_RECRUITER",
      payload: {
        ...(currentRecruiter || {}),
        company: {
          ...(currentRecruiter.company || {}),
          kanban_pending_cards_count: pendingCount,
        },
      },
    });
  }, [cardsByColumnId, columns, currentRecruiter, dispatch]);

  return (
    <Box 
      ref={boardScrollRef} 
      sx={{ 
        marginTop: "32px", 
        overflowX: "auto", 
        paddingBottom: "8px",
        marginRight: "calc(-50vw + 52%)",
        paddingRight: "calc(50vw - 50%)",
        width: "auto",
      }}
    >
      <Box sx={{ display: "flex", gap: "14px", alignItems: "flex-start", minHeight: "420px" }}>
        {(columns || []).map((col, colIndex) => {
          const totalCount = columnCountsById?.[col.id];
          const columnCards = normalized[toKey(col.id)] || [];
          const colCardDropTarget =
            cardDropTarget && String(cardDropTarget.columnId) === String(col.id) ? cardDropTarget : null;

          return (
            <React.Fragment key={col.id}>
              <ColumnDropIndicator
                visible={columnDropTarget?.columnId === col.id && columnDropTarget?.edge === "left"}
              />
              <KanbanColumn
                column={col}
                dragDisabled={dragDisabled}
                columnDragDisabled={PROTECTED_COLUMN_KEYS.includes(col.key)}
                isDragging={draggingColumnId === col.id}
                setColumns={setColumns}
                editingColumnId={editingColumnId}
                setEditingColumnId={setEditingColumnId}
                cards={columnCards}
                columnStats={columnStatsById?.[col.id] || {}}
                columnCount={typeof totalCount === "number" ? totalCount : columnCards.length}
                cardDropTarget={colCardDropTarget}
                renderCard={(card, idx) => (
                  <KanbanCard
                    key={card.id}
                    card={card}
                    columnId={toKey(col.id)}
                    columnKey={col.key}
                    index={idx}
                    dragDisabled={dragDisabled}
                    draggingCardId={draggingCardId}
                    advancing={advancingCardId === card.id}
                    onMoveForward={advanceJobApplication}
                    moveForwardLabel={col.key === "pending" ? pendingNextColumnName : null}
                    favorites={currentRecruiter?.favorite_candidate_ids || []}
                    onToggleFavorite={handleToggleFavorite}
                    onOpenDialog={openDialogForCard}
                    onJobApplicationClosed={handleCardApplicationClosed}
                    onKanbanMetaUpdate={onKanbanMetaUpdate}
                  />
                )}
                kanbanColumnsLength={columns.length}
              />
              {colIndex === (columns || []).length - 1 && (
                <ColumnDropIndicator
                  visible={columnDropTarget?.columnId === col.id && columnDropTarget?.edge === "right"}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {dragDisabled ? (
        <Typography sx={{ mt: 1, fontSize: "12px", opacity: 0.65, color: theme.palette.secondary.black, fontFamily: "Segoe UI Regular, sans-serif" }}>
          <FormattedMessage id="recruiter.kanban.drag_and_drop_disabled_while_searching" />
        </Typography>
      ) : null}

      {dialogJobApplication && dialogCandidate ? (
        <CandidateDialog
          isModal
          candidate={dialogCandidate}
          jobApplication={dialogJobApplication}
          jobApplications={dialogJobApplication?.job_applications || []}
          onClose={() => {
            setIsDialogOpen(false);
            setDialogJobApplication(null);
            setDialogCandidate(null);
          }}
          isDialogOpen={isDialogOpen}
          setIsDialogOpen={setIsDialogOpen}
          columns={(columns || []).map((col) => ({
            ...col,
            count:
              (typeof columnCountsById?.[col.id] === "number" ? columnCountsById[col.id] : null) ??
              (Array.isArray(normalized?.[toKey(col.id)]) ? normalized[toKey(col.id)].length : 0),
          }))}
          currentColumnId={dialogJobApplication?.kanban_column_id}
          onMoveToColumn={async (kanbanCard, toColumnId, hiredJobApplicationId = null) => {
            const toColumn = columns.find((c) => c.id === toColumnId);
            if (!toColumn) return;

            const fromColumnId = toKey(kanbanCard.kanban_column_id);
            const toColKey = toKey(toColumnId);
            const isMovingToHired = toColumn.key === "hired";

            let updatedJobApplications = kanbanCard.job_applications || [];
            if (isMovingToHired && hiredJobApplicationId) {
              const hiredStatus = kanbanCard?.is_external ? "hired" : "accepted";
              updatedJobApplications = updatedJobApplications
                .map((app) => ({
                  ...app,
                  status: app.id === hiredJobApplicationId ? hiredStatus : "closed",
                }))
                .filter((app) => app.id === hiredJobApplicationId);
            }

            setCardsByColumnId((prev) => {
              const next = { ...(prev || {}) };
              const fromArr = Array.isArray(next[fromColumnId]) ? [...next[fromColumnId]] : [];
              const toArr = Array.isArray(next[toColKey]) ? [...next[toColKey]] : [];

              const idx = fromArr.findIndex((c) => c?.id === kanbanCard.id);
              if (idx === -1) return prev;

              const [card] = fromArr.splice(idx, 1);
              toArr.unshift({
                ...card,
                kanban_column_id: toColumnId,
                last_moved_at: new Date().toISOString(),
                ...(isMovingToHired && hiredJobApplicationId ? { job_applications: updatedJobApplications } : {}),
              });

              next[fromColumnId] = fromArr;
              next[toColKey] = toArr;
              return next;
            });

            const payload = {
              kanban_column_id: toColumnId,
              position: 0,
              authenticity_token: authenticityToken,
            };
            
            if (hiredJobApplicationId) {
              payload.hired_job_application_id = hiredJobApplicationId;
            }

            const moveRes = await sh.put(`/move_kanban_card/${kanbanCard.id}`, payload);
            const meta = moveRes?.data?.kanban;
            if (meta && typeof onKanbanMetaUpdate === "function") onKanbanMetaUpdate(meta);
            if (typeof onBoardMutated === "function") onBoardMutated();
          }}
          onJobApplicationClosed={(closed) => handleCardApplicationClosed(closed, dialogJobApplication, dialogCandidate)}
          onJobApplicationReopened={(reopened) => handleCardApplicationReopened(reopened, dialogJobApplication)}
        />
      ) : null}

      {/* Hired Position Selection Modal */}
      <HiredPositionSelectionModal
        open={hiredModalOpen}
        onClose={handleHiredCancel}
        jobApplications={hiredModalData?.card?.job_applications || []}
        selectedJobApplicationId={selectedHiredAppId}
        onSelectJobApplication={setSelectedHiredAppId}
        onConfirm={handleHiredConfirm}
        loading={hiredModalLoading}
      />
    </Box>
  );
};

export default KanbanBoard;


